import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import { MuiOtpInput } from 'mui-one-time-password-input';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import npsfLogoWhite from '../assets/npsf-logo-white.png';
import { sendOTP, verifyOTP, resetPassword } from '../api/athena4p-api';
import '../styles/RequestForm.css';

type Severity = 'error' | 'success' | 'info' | 'warning' | undefined;

type Step = 'email' | 'otp' | 'password' | 'success';

const ForgotPassword: React.FC = () => {
  // Step management
  const [currentStep, setCurrentStep] = useState<Step>('email');

  // Form fields
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // OTP states
  const [otp, setOtp] = useState('');
  const [otpStatusMessage, setOtpStatusMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<Severity>('info');

  // Error and loading states
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  // Refs for focus management
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }

    if (password.length > 256) {
      return 'Password must be 256 characters or less';
    }

    // Entra External ID requires 3 out of 4: uppercase, lowercase, numbers, symbols
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[^a-zA-Z0-9]/.test(password);
    const complexityCount = [
      hasLowercase,
      hasUppercase,
      hasNumber,
      hasSymbol,
    ].filter(Boolean).length;

    if (complexityCount < 3) {
      return 'Password must contain at least 3 of the following: uppercase letters, lowercase letters, numbers, symbols';
    }

    return null;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Email address is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    await sendOtpToUser();
  };

  const sendOtpToUser = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const sendOTPResponse = await sendOTP({ email });
      if (sendOTPResponse && sendOTPResponse.status === 200) {
        setAlertSeverity('success');
        setOtpStatusMessage(
          `Verification code sent to ${email}. If not received, please check your SPAM folder before resending.`
        );
        setCurrentStep('otp');
      }
    } catch (err: any) {
      setAlertSeverity('error');
      if (err.response?.status === 404) {
        setError('No account found with this email address.');
      } else {
        setError('Failed to send verification code. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setOtp('');
    await sendOtpToUser();
  };

  const handleOtpChange = (newValue: string) => {
    setOtp(newValue);
  };

  const handleVerifyOtp = async () => {
    setIsVerifying(true);
    setOtpStatusMessage('');

    try {
      const verifyOTPResponse = await verifyOTP({ email, otp });
      if (verifyOTPResponse && verifyOTPResponse.status === 200) {
        setAlertSeverity('success');
        setOtpStatusMessage('Email verified successfully!');
        setCurrentStep('password');
        // Focus password field after transition
        setTimeout(() => {
          passwordInputRef.current?.focus();
        }, 100);
      }
    } catch (err: any) {
      setAlertSeverity('error');
      if (err.response) {
        setOtpStatusMessage(
          err.response.data ?? 'Invalid verification code. Please try again.'
        );
      } else {
        setOtpStatusMessage('Something went wrong. Please try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setNewPassword(password);

    if (password) {
      const validationError = validatePassword(password);
      setPasswordError(!!validationError);
    } else {
      setPasswordError(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const passwordValidationError = validatePassword(newPassword);
    if (passwordValidationError) {
      setError(passwordValidationError);
      setPasswordError(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const resetResponse = await resetPassword({
        email,
        newPassword,
      });

      if (resetResponse && resetResponse.status === 200) {
        setCurrentStep('success');
      }
    } catch (err: any) {
      setAlertSeverity('error');
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to reset password. Please try again or contact support.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToEmail = () => {
    setCurrentStep('email');
    setOtp('');
    setOtpStatusMessage('');
    setError('');
  };

  const handleBackFromPassword = () => {
    setCurrentStep('email');
    setOtp('');
    setOtpStatusMessage('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(false);
    setError('');
  };

  // Success screen
  if (currentStep === 'success') {
    return (
      <div id='request-form-root'>
        <div className='blue-section'>
          <img
            src={npsfLogoWhite}
            alt='NPSF Logo'
            style={{ width: '150px', height: 'auto', marginBottom: '1rem' }}
          />
          <h1>Password Reset Complete</h1>
          <p>Your password has been successfully updated</p>
        </div>
        <div className='form-wrapper'>
          <Paper elevation={3} sx={{ padding: '2rem', marginTop: '2rem' }}>
            <Stack spacing={3} alignItems='center'>
              <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50' }} />
              <Typography variant='h5' component='h2' textAlign='center'>
                Password Reset Successful
              </Typography>
              <Typography
                variant='body1'
                color='text.secondary'
                textAlign='center'
              >
                Your password has been successfully reset. You can now sign in
                to Athena using your new password.
              </Typography>

              <Divider sx={{ width: '100%', my: 2 }} />

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  width: '100%',
                }}
              >
                <Typography
                  variant='body2'
                  color='text.secondary'
                  textAlign='center'
                >
                  Need help? Contact:{' '}
                  <a
                    href='mailto:athena@npsfoundation.org'
                    style={{
                      color: '#002855',
                      textDecoration: 'none',
                      fontWeight: 'bold',
                    }}
                  >
                    athena@npsfoundation.org
                  </a>
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </div>
      </div>
    );
  }

  return (
    <div id='request-form-root'>
      <div className='blue-section'>
        <img
          src={npsfLogoWhite}
          alt='NPSF Logo'
          style={{ width: '150px', height: 'auto', marginBottom: '1rem' }}
        />
        <h1>Reset Your Password</h1>
        <p>Verify your identity to create a new password</p>
      </div>
      <div className='form-wrapper'>
        <Paper elevation={3} sx={{ padding: '2rem', marginTop: '2rem' }}>
          {/* Step 1: Email Entry */}
          {currentStep === 'email' && (
            <form onSubmit={handleEmailSubmit}>
              <Stack spacing={3}>
                <Typography variant='h5' component='h2'>
                  Forgot Password
                </Typography>

                <Typography variant='body2' color='text.secondary'>
                  Enter your email address and we'll send you a verification
                  code to reset your password.
                </Typography>

                {error && <Alert severity='error'>{error}</Alert>}

                <TextField
                  label='Email Address'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  placeholder='your.email@example.com'
                  helperText='Enter the email address associated with your Athena account'
                  autoFocus
                />

                <Button
                  type='submit'
                  variant='contained'
                  size='large'
                  startIcon={<LockResetIcon />}
                  disabled={isSubmitting}
                  sx={{
                    backgroundColor: '#002855',
                    '&:hover': {
                      backgroundColor: '#003366',
                    },
                  }}
                >
                  {isSubmitting ? 'Sending Code...' : 'Send Verification Code'}
                </Button>
              </Stack>
            </form>
          )}

          {/* Step 2: OTP Verification */}
          {currentStep === 'otp' && (
            <Stack spacing={3}>
              <Box display='flex' alignItems='center'>
                <IconButton onClick={handleBackToEmail} sx={{ mr: 1 }}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant='h5' component='h2'>
                  Verify Your Email
                </Typography>
              </Box>

              <Typography variant='body2' color='text.secondary'>
                We've sent a 6-digit verification code to{' '}
                <strong>{email}</strong>. Enter the code below to continue.
              </Typography>

              {otpStatusMessage && (
                <Alert severity={alertSeverity}>{otpStatusMessage}</Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <MuiOtpInput
                  value={otp}
                  onChange={handleOtpChange}
                  length={6}
                  autoFocus
                />
              </Box>

              <Stack direction='row' spacing={2} justifyContent='center'>
                <Button
                  onClick={handleResendOtp}
                  color='secondary'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Resend Code'}
                </Button>
                <Button
                  onClick={handleVerifyOtp}
                  variant='contained'
                  disabled={isVerifying || otp.length !== 6}
                  sx={{
                    backgroundColor: '#002855',
                    '&:hover': {
                      backgroundColor: '#003366',
                    },
                  }}
                >
                  {isVerifying ? 'Verifying...' : 'Verify Code'}
                </Button>
              </Stack>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  Didn't receive the code? Check your spam folder or{' '}
                  <a
                    href='mailto:athena@npsfoundation.org'
                    style={{
                      color: '#002855',
                      textDecoration: 'none',
                      fontWeight: 'bold',
                    }}
                  >
                    contact support
                  </a>
                  .
                </Typography>
              </Box>
            </Stack>
          )}

          {/* Step 3: New Password Entry */}
          {currentStep === 'password' && (
            <form onSubmit={handlePasswordSubmit}>
              <Stack spacing={3}>
                <Box display='flex' alignItems='center'>
                  <IconButton onClick={handleBackFromPassword} sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant='h5' component='h2'>
                    Create New Password
                  </Typography>
                </Box>

                <Alert severity='success'>
                  Email verified for <strong>{email}</strong>
                </Alert>

                <Typography variant='body2' color='text.secondary'>
                  Please enter your new password below.
                </Typography>

                {error && <Alert severity='error'>{error}</Alert>}

                <TextField
                  inputRef={passwordInputRef}
                  label='New Password'
                  type='password'
                  value={newPassword}
                  onChange={handlePasswordChange}
                  required
                  fullWidth
                  error={passwordError}
                  helperText='8-256 characters with at least 3 of: uppercase, lowercase, numbers, symbols'
                />

                <TextField
                  label='Confirm Password'
                  type='password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  fullWidth
                  error={confirmPassword !== '' && newPassword !== confirmPassword}
                  helperText={
                    confirmPassword !== '' && newPassword !== confirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                />

                <Button
                  type='submit'
                  variant='contained'
                  size='large'
                  startIcon={<LockResetIcon />}
                  disabled={isSubmitting || passwordError}
                  sx={{
                    backgroundColor: '#002855',
                    '&:hover': {
                      backgroundColor: '#003366',
                    },
                  }}
                >
                  {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                </Button>

                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant='body2' color='text.secondary'>
                    Need help?{' '}
                    <a
                      href='mailto:athena@npsfoundation.org'
                      style={{
                        color: '#002855',
                        textDecoration: 'none',
                        fontWeight: 'bold',
                      }}
                    >
                      Contact athena@npsfoundation.org
                    </a>
                  </Typography>
                </Box>
              </Stack>
            </form>
          )}
        </Paper>
      </div>
    </div>
  );
};

export default ForgotPassword;
